import React, { useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  styled,
  Button,
  Link,
  Waves,
  Layout
} from '@pergas-common/pergas-components'
import {
  Add,
  CheckBook,
  Company
} from '@pergas-common/pergas-icons'
import { Save } from '@mui/icons-material'
import { selectLocale } from '../../../redux/locale/selectors'
import { makeSelectorPermissionProject } from '../../../redux/permission/selectors'
import { useAddProjectMutation, useUpdateProjectMutation, useGetProjectByIdQuery } from '../../../redux/project/hooks'
import TopbarNavigation from '../../TopbarNavigation'
import { usePermissionRoute } from '../../../hooks/usePermission'
import useUrlQuery from '../../../hooks/useUrlQuery'
import AsyncLoader from '../../AsyncLoader'
import ProjectForm from './form'
import Buttons from './buttons'
import dayjs from 'dayjs'

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

const validate = (values) => {
  const errors = {}
  if (!values.name) {
    errors.name = 'Required'
  }
  if (!values.collection_id) {
    errors.collection_id = 'Required'
  }
  return errors
}

const isRepairSharepointStatus = (item = {}) => {
  const statusId = Number(item?.sharepoint_status_id)
  const status = String(item?.sharepoint_status || '').toLowerCase()
  return statusId === 4 || status === 'error'
}

const isSharepointUpdatingStatus = (item = {}) => {
  const statusId = Number(item?.sharepoint_status_id)
  const status = String(item?.sharepoint_status || '').toLowerCase()
  return statusId === 2 || statusId === 3 || status === 'creating' || status === 'updating'
}

const useInitialValues = (project = {}) => {
  const { state } = useLocation()
  const {
    name = '',
    description = '',
    ended_at = null,
    started_at = dayjs(),
    expires_at = dayjs().add(1, 'day'),
    teams_url = '',
    person_role = [],
    object_type_name = '',
    object_type_id = 0,
    collection_name = state?.contact?.name || '',
    collection_id = state?.contact?.id || 0,
    tags = [],
    eid = ''
  } = project
  return {
    name,
    description: description ?? '',
    ended_at,
    started_at: dayjs(started_at).format('YYYY-MM-DD'),
    expires_at: dayjs(expires_at).format('YYYY-MM-DD'),
    teams_url,
    person_role,
    object_type_id,
    object_type_name,
    collection_name,
    collection_id,
    tags,
    eid
  }
}

export function useFormikValues (cb) {
  const onSubmit = useCallback(async (values) => {
    let payload = {
      ...values
    }
    if (values.expires_at === '' || values.expires_at === 'Invalid Date') {
      payload = { ...payload, expires_at: null }
    }

    if (values.started_at === '' || values.started_at === 'Invalid Date') {
      payload = { ...payload, started_at: null }
    }

    cb(payload)
  }, [cb])
  return onSubmit
}

const New = () => {
  const navigate = useNavigate()

  const locale = useSelector(selectLocale)
  const selectByPermissionProject = useMemo(makeSelectorPermissionProject, [])
  const permissions = useSelector(selectByPermissionProject)

  const { metadata, mutation, isLoading, isSuccessful } = useAddProjectMutation()

  const formData = useInitialValues()
  const onSubmitFormik = useFormikValues(mutation)
  usePermissionRoute(permissions, ['canCreate'])

  useEffect(() => {
    if (!isLoading && isSuccessful) {
      navigate(`/projects/edit?id=${metadata?.id}`, { replace: true })
    }
  }, [metadata, isSuccessful])

  if (isLoading) {
    return (
      <Layout.Main>
        <Layout.Aside>
          <Center>
            <Waves width={40} height={40} />
          </Center>
        </Layout.Aside>
      </Layout.Main>
    )
  }

  return (
    <Formik initialValues={formData} onSubmit={onSubmitFormik} validate={validate}>
      {(props) => {
        const { inputError, isSubmitting, dirty } = props
        const submit = (!inputError && !isSubmitting && dirty && !isLoading)
        const submitAsyncLoader = <AsyncLoader loading={isSubmitting || isLoading}><Save style={{ color: '#34b3e1', width: 20, height: 20 }} /></AsyncLoader>
        return (
          <>
            <TopbarNavigation title={locale.project} icon={<Company width={20} height={20} />} />
            <ProjectForm disable={false} {...props}>
              <Buttons {...props}>
                <Button disabled={!submit} type='submit' form='submitProject'><span>{locale.save}</span>{submitAsyncLoader}</Button>
              </Buttons>
            </ProjectForm>
          </>
        )
      }}
    </Formik>
  )
}

const View = () => {
  const navigate = useNavigate()
  const query = useUrlQuery()
  const id = query.get('id')

  const selectByPermissionProject = useMemo(makeSelectorPermissionProject, [])
  const permissions = useSelector(selectByPermissionProject)

  const locale = useSelector(selectLocale)
  const { data, refetch, isLoading } = useGetProjectByIdQuery(id)

  const { mutation: updateMutation, isLoading: isMutating, isSuccessful: isSuccessfulUpdate } = useUpdateProjectMutation()

  const formData = useInitialValues(data)
  const isRepairAction = isRepairSharepointStatus(data)
  usePermissionRoute(permissions, ['canUpdate'])

  const onSubmitFormik = useFormikValues(updateMutation)

  const onSubmit = useCallback((values) => {
    const payload = isRepairAction
      ? { ...values, sharepoint_status_id: 4 }
      : values
    onSubmitFormik({ id, ...payload })
  }, [id, isRepairAction, onSubmitFormik])

  useEffect(() => {
    if (isSuccessfulUpdate) {
      refetch()
    }
  }, [isSuccessfulUpdate, refetch])

  if (isLoading) {
    return (
      <Layout.Main>
        <Layout.Aside>
          <Center>
            <Waves width={40} height={40} />
          </Center>
        </Layout.Aside>
      </Layout.Main>
    )
  }

  const createLink = permissions.canCreate && <Link onClickHandler={() => { navigate('/projects/new') }}><Add width={20} height={20} color='#28afe0' /></Link>
  return (
    <Formik
      initialValues={formData}
      onSubmit={onSubmit}
      validate={isRepairAction ? () => ({}) : validate}
    >
      {(props) => {
        const { inputError, isSubmitting, dirty } = props
        const isSharepointUpdating = isSharepointUpdatingStatus(data)
        const disableSubmit = isRepairAction ? false : (inputError || isSubmitting || !dirty || isMutating)
        const isButtonLoading = isRepairAction ? (isSubmitting || isMutating || isSharepointUpdating) : (isSubmitting || isMutating)
        const submitAsyncLoader = <AsyncLoader loading={isButtonLoading}><Save style={{ color: '#34b3e1', width: 20, height: 20 }} /></AsyncLoader>
        return (
          <>
            <TopbarNavigation title={locale.project} icon={<CheckBook width={20} height={20} />} link={createLink} />
            <ProjectForm disable={false} {...props} original={data}>
              <Buttons {...props} groupId={data?.group_id}>
                <Button disabled={disableSubmit} type='submit' form='submitProject'><span>{isRepairAction ? locale.repair : locale.save}</span>{submitAsyncLoader}</Button>
              </Buttons>
            </ProjectForm>
          </>
        )
      }}
    </Formik>
  )
}

export default { View, New }
